import { Button, Input } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import Link from "next/link";
import React, { ChangeEvent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Toggle } from "react-powerplug";
import { IGroupQuery } from "~/lib/queries/__generated__/GroupQuery.generated";
import { ICompany, IProduct } from "~/types/types";
import mediaQueries from "~/components/mediaQueries";
import { NoProducts } from "~/components/profile/EmptyBlocks";
import { Margin } from "~/components/spacings";
import Dotdotdot from "~/components/text/Dotdotdot";
import GroupProductsModal from "~/components/group/GroupProductsModal";
import NoResultsFound from "~/components/group/NoResultsFound";

interface IProductsProps {
  products: (Pick<IProduct, "_id" | "slug" | "logo" | "name"> & { company: Pick<ICompany, "slug" | "name"> | null })[];
  group: NonNullable<IGroupQuery["groupBySlug"]>;
}

export default function Products({ products, group }: IProductsProps) {
  const [filter, setFilter] = useState("");
  const intl = useIntl();

  const filtered = products.filter((product) => {
    if (!product.name || !filter) {
      return true;
    }

    return product.name.toLowerCase().includes(filter.toLowerCase());
  });

  if (products.length === 0 && group.viewerCanHeadContent) {
    return (
      <Toggle>
        {({ on: isOpen, toggle }) => (
          <>
            <GroupProductsModal group={group} toggleOpen={toggle} isOpen={isOpen} />

            {group.viewerIsMember && <NoProducts onAddProduct={toggle} data-intercom-target="Group Products Box" />}
          </>
        )}
      </Toggle>
    );
  }

  return (
    <div
      data-intercom-target="Group Products"
      css={{
        borderRadius: 5,
        border: `1px solid ${palette.lightestGray}`,
        background: "#fff",
      }}
    >
      <div>
        <div
          css={{
            padding: 16,
          }}
        >
          <Margin
            mb={16}
            css={{
              display: "flex",
            }}
          >
            <Input
              placeholder={intl.formatMessage({ defaultMessage: "Search for products...", id: "X8m9DF" })}
              value={filter}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
            />
            {group.viewerIsMember && (
              <Toggle>
                {({ on: isOpen, toggle }) => (
                  <>
                    <GroupProductsModal group={group} toggleOpen={toggle} isOpen={isOpen} />

                    <Button
                      theme="link"
                      color={palette.innovatorBlue}
                      textColor={palette.innovatorBlue}
                      onClick={toggle}
                      css={{
                        color: palette.innovatorBlue,
                        marginLeft: 16,
                        textTransform: "uppercase",
                      }}
                    >
                      <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
                    </Button>
                  </>
                )}
              </Toggle>
            )}
          </Margin>

          <div
            css={mediaQueries({
              display: "grid",
              gridTemplateColumns: ["1fr", "1fr 1fr 1fr"],
              gridColumnGap: 32,
              gridRowGap: 32,
            })}
          >
            {filtered.length === 0 && filter.length > 0 && <NoResultsFound />}

            {filtered.map((product) => (
              <div
                key={product._id}
                css={{
                  display: "flex",
                  overflow: "hidden",
                }}
              >
                <ProfilePicture
                  key={product._id}
                  image={product.logo}
                  name={product.name}
                  size="xsmall"
                  css={{
                    borderRadius: 6,
                    "& > img": {
                      borderRadius: 6,
                      padding: 0,
                    },
                    padding: 0,
                  }}
                />

                <div
                  css={{
                    marginLeft: 12,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <p
                    css={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      margin: 0,
                      fontSize: 18,
                      fontWeight: "bold",
                      color: palette.primary,
                    }}
                  >
                    {product.name}
                  </p>
                  {product.company && (
                    <Link
                      href="/vendors/[vendorSlug]"
                      as={`/vendors/${product.company.slug}`}
                      passHref={true}
                      legacyBehavior={true}
                    >
                      <a
                        css={{
                          fontSize: 13,
                          color: palette.sealBlue,
                          lineHeight: "17px",
                        }}
                      >
                        <Dotdotdot clamp={1}>
                          <FormattedMessage
                            defaultMessage="from {name}"
                            id="wlSBPQ"
                            values={{ name: product.company.name }}
                          />
                        </Dotdotdot>
                      </a>
                    </Link>
                  )}

                  <p
                    css={{
                      fontSize: 13,
                      color: palette.sealBlue,
                      lineHeight: "17px",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
